import React, { useState } from "react"
import Modal from "react-modal"
import { useTemplateData } from "src/Context/TemplateData.context"
import Button from "src/component/button/button"
import { createTemplate } from "src/api/template"
import CustomInput from "src/component/CustomInput/CustomInput"
import CustomSelect from "src/component/customSelect/customSelect"
import { IoMdClose } from "react-icons/io"
import { FiX } from "react-icons/fi"
import { FaClipboard, FaFolder, FaSpinner } from "react-icons/fa"
import { successMessage } from "src/utils/Helper"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { GRADES } from "src/utils/grade"
import { SingleValue } from "react-select"

const SaveTemplateModal: React.FC = () => {
  const {
    subjects,
    isSaveModalOpen,
    setIsSaveModalOpen,
    topicGroup,
    clearFormValues,
    selectedSubject,
    setSelectedSubject,
    name,
    setName,
    setThumbnail,
    setThumbnailPreviewUrl,
    thumbnail,
    thumbnailPreviewUrl,
    pageType,
  } = useTemplateData()

  const [grade, setGrade] = useState<string>("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [publishLoading, setPublishLoading] = useState(false)
  const [errors, setErrors] = useState<{ grade?: string }>({})
  const { templateId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()

  // Handle file input change
  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    setThumbnail(file)
    if (file) {
      setThumbnailPreviewUrl(URL.createObjectURL(file))
    } else {
      setThumbnailPreviewUrl(null)
    }
  }

  // Handle removing the thumbnail
  const handleRemoveThumbnail = () => {
    setThumbnail(null)
    setThumbnailPreviewUrl(null)
    const fileInput = document.getElementById(
      "template-thumbnail-input"
    ) as HTMLInputElement
    if (fileInput) {
      fileInput.value = ""
    }
  }

  const handleSubmit = async (status: string) => {
    setError(null)
    setErrors({})

    if (!name.trim()) {
      setError("Please provide a name.")
      return
    }

    if (!grade) {
      setErrors((prev) => ({ ...prev, grade: "Please select a level." }))
      return
    }

    if (!selectedSubject) {
      setError("Please select a subject.")
      return
    }
    if (!thumbnail) {
      setError("Please select a thumnail image.")
      return
    }

    try {
      if (status === "published") {
        setPublishLoading(true)
      } else {
        setLoading(true)
      }

      const payload = {
        name,
        grade,
        subject: selectedSubject,
        groups: topicGroup,
        thumbnail,
        status,
      }

      console.log({ payload }, "this is data payload")

      let response
      if (pageType === "edit") {
        if (!templateId) {
          setError("Missing template ID for edit.")
          return
        }

        const { editTemplate } = await import("src/api/template")
        await editTemplate(templateId, payload)
        successMessage("Template updated successfully!")
      } else {
        response = await createTemplate(payload)
        successMessage("Template created successfully!")
      }

      clearFormValues()
      setName("")
      setGrade("")
      setThumbnail(null)
      setThumbnailPreviewUrl(null)
      setIsSaveModalOpen(false)

      if (
        location.pathname === "/exams/create-with-template" &&
        status === "published" &&
        response?.data?.template_id
      ) {
        navigate(`/templates/template-use/${response.data.template_id}`)
      } else {
        navigate("/templates")
      }
    } catch (err: any) {
      console.error("Error saving template:", err)
      setError(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          "Failed to save template."
      )
    } finally {
      setLoading(false)
      setPublishLoading(false)
    }
  }

  const subjectOptions = subjects.map((sub) => ({
    label: sub.name,
    value: String(sub.id),
  }))

  // Find the currently selected subject to display in CustomSelect
  const currentSelectedSubjectOption = subjects.find(
    (item) => item?.id === selectedSubject
  )

  // Find current selected grade option to pass to CustomSelect
  const currentSelectedGradeOption =
    GRADES.find((g) => g.value === grade) || null

  return (
    <Modal
      isOpen={isSaveModalOpen}
      onRequestClose={() => setIsSaveModalOpen(false)}
      overlayClassName='fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4'
      className='bg-white rounded-md shadow-md w-full max-w-[700px] max-h-[calc(100vh-2rem)] outline-none flex flex-col'
    >
      <div className='p-6 overflow-y-auto'>
        <h2 className='text-xl font-bold mb-4 text-gray-800'>Save as Template</h2>

        {error && (
          <div className='mb-4 text-red-600 text-sm font-medium'>{error}</div>
        )}

        <div className='flex flex-col gap-4 mb-4 md:flex-row'>
          <div className='flex-1 min-w-0'>
            <label className='block mb-2 text-sm text-gray-700'>
              Name this template
            </label>
            <CustomInput
              type='text'
              placeholder='Enter template name'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className='flex-1 min-w-0'>
            <label className='block mb-2 text-sm text-gray-700'>Level</label>
            <CustomSelect
              placeholder='Select Level'
              data={GRADES}
              onChange={(
                value: SingleValue<{ label: string; value: string }>
              ) => {
                setGrade(value?.value || "")
                setErrors((prev) => ({ ...prev, grade: "" }))
              }}
              value={currentSelectedGradeOption}
              error={errors.grade}
            />
          </div>

          <div className='flex-1 min-w-0'>
            <label className='block mb-2 text-sm text-gray-700'>Subject</label>
            <CustomSelect
              placeholder='Select a subject'
              data={subjectOptions}
              onChange={(
                selectedOption: { label: string; value: string } | null
              ) => setSelectedSubject(selectedOption?.value || "")}
              value={
                currentSelectedSubjectOption
                  ? {
                      label: currentSelectedSubjectOption.name,
                      value: currentSelectedSubjectOption.id,
                    }
                  : null
              }
            />
          </div>
        </div>

        <label className='block mt-4 mb-2 text-sm text-gray-700'>
          Template Thumbnail
        </label>

        {thumbnailPreviewUrl ? (
          <div className='relative w-full h-48 rounded-md overflow-hidden mb-6 border border-gray-300'>
            <img
              src={thumbnailPreviewUrl}
              alt='Template Thumbnail Preview'
              className='w-full h-full object-cover'
            />
            <button
              onClick={handleRemoveThumbnail}
              className='absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 cursor-pointer hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50'
              aria-label='Remove thumbnail'
            >
              <IoMdClose size={18} />
            </button>
          </div>
        ) : (
          <input
            id='template-thumbnail-input'
            type='file'
            accept='image/*'
            onChange={handleThumbnailChange}
            className='mb-6 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-100 file:text-red-700 hover:file:bg-blue-100'
          />
        )}
      </div>

      <div className='flex flex-wrap justify-end gap-3 p-6 pt-4 border-t border-gray-200'>
        {pageType === "create" && (
          <Button
            text={publishLoading ? "Publishing..." : "Publish"}
            type='secondary'
            icon={
              publishLoading ? (
                <FaSpinner className='animate-spin' />
              ) : (
                <FaClipboard />
              )
            }
            className='text-red-700 border-red-700 border-[1px] bg-transparent'
            onClick={() => handleSubmit("published")}
            disabled={loading || publishLoading}
          />
        )}
        <Button
          text={loading ? "Saving..." : "Save"}
          type='primary'
          onClick={() => handleSubmit("draft")}
          disabled={loading || publishLoading}
          icon={loading ? <FaSpinner className='animate-spin' /> : <FaFolder />}
        />
        <Button
          icon={<FiX />}
          text='Cancel'
          type='link'
          onClick={() => {
            setIsSaveModalOpen(false)
            setError(null)
          }}
          disabled={loading || publishLoading}
          className='text-red-700 bg-transparent text-[16px]'
        />
      </div>
    </Modal>
  )
}

export default SaveTemplateModal
